import { useTranslation } from 'react-i18next';

import { Checkbox } from '@/components/ui/checkbox';
import { cn, formatDecimal } from '@/lib/utils';

export type VatRateCellProps = {
  /** Deal item id, used to pair the margin checkbox with its label. */
  dealItemId: string;
  /**
   * Rate to render: a number for a real rate, a string for an aggregated parent
   * marker (`'≠'`), `null` when there is nothing to show.
   */
  displayValue: number | string | null | undefined;
  /** Drives the rate display: a margin-VAT item shows no percentage. */
  hasVatMargin: boolean;
  /**
   * Checked state of the margin checkbox. On a parent row it aggregates the
   * children, which is why it is not simply {@link hasVatMargin}.
   */
  isMarginChecked: boolean;
  isParent: boolean;
  isOfferLocked: boolean;
  isRecentlyModified: boolean;
  onToggleVatMargin: (dealItemId: string) => void;
  /**
   * Whether the "TVA sur marge" checkbox is offered. The table needs it inline;
   * a review surface showing the rate as evidence does not.
   */
  showMarginToggle?: boolean;
};

/**
 * The VAT cell: the rate plus the "TVA sur marge" checkbox that goes with it.
 *
 * Shared between the main table column and the VAT review dialog so both render
 * the same control — a rate shown one way in the table and another in the dialog
 * would be a bug waiting to happen, since the dialog exists precisely to judge
 * that rate.
 */
export const VatRateCell: React.FC<VatRateCellProps> = ({
  dealItemId,
  displayValue,
  hasVatMargin,
  isMarginChecked,
  isParent,
  isOfferLocked,
  isRecentlyModified,
  onToggleVatMargin,
  showMarginToggle = true,
}) => {
  const { t } = useTranslation();

  return (
    <div
      className={cn(
        'h-full w-full text-right',
        (isParent || isOfferLocked) && 'opacity-50',
        isParent || !isOfferLocked ? 'cursor-pointer' : 'cursor-not-allowed',
        isRecentlyModified && 'border-l-2 border-blue-500 bg-blue-50'
      )}
    >
      <div className="flex h-full w-full items-center justify-between px-2 text-sm">
        <span title={t('kpi.vat_tooltip')}>
          {typeof displayValue === 'number' && !hasVatMargin
            ? `${formatDecimal(displayValue)}%`
            : typeof displayValue === 'string'
              ? displayValue
              : '—'}
        </span>
        {showMarginToggle && (
          <div className="flex items-center gap-1">
            <Checkbox
              defaultChecked={isMarginChecked}
              onCheckedChange={() => onToggleVatMargin(dealItemId)}
              // A locked offer rejects the mutation server-side
              // (`dealNotEditable`): leaving the box clickable only bought the
              // user an optimistic flip followed by a rollback and a toast.
              disabled={isParent || isOfferLocked}
              id={`${isParent ? 'parent-' : ''}tvasm-${dealItemId}`}
            />
            <label
              className="cursor-pointer text-xs"
              htmlFor={`${isParent ? 'parent-' : ''}tvasm-${dealItemId}`}
            >
              {t('kpi.vat_ms')}
            </label>
          </div>
        )}
      </div>
    </div>
  );
};
